import { observable, when } from "mobx";
import _ from "lodash";
import { DomainStore } from "./domainStore";
import { getQuery } from "domain/helpers/query";
import {
  TeamMemberScopes,
  TeamMemberScopeTypes,
} from "encharge-domain/definitions/TeamMember";

export class PermissionsStore {
  rootStore: DomainStore;
  constructor(rootStore: DomainStore) {
    this.rootStore = rootStore;

    const query = getQuery();
    if (isSharedFlow()) {
      this.readOnlyAuthToken = query["flow-read-auth"];
      this.readOnlyFlow = true;
    }
  }

  @observable
  readOnlyFlow: boolean = false;

  @observable
  readOnlyAuthToken: undefined | string;

  @observable
  hasLoadedScopes() {
    return Boolean(this.rootStore.accountStore.account);
  }

  // Triggers a passed function when the scopes has loaded
  async onLoadedScopes(func: () => void) {
    await when(() => this.hasLoadedScopes());
    func();
  }

  @observable
  checkScope(scope: TeamMemberScopeTypes) {
    return this.rootStore.accountStore.checkScope(scope);
  }
}

export const isSharedFlow = () => {
  return Boolean(getQuery()?.["flow-read-auth"]);
};

type TeamMemberScopesDisplay = TeamMemberScopeDisplay[];
export type TeamMemberScopeDisplay = {
  id: TeamMemberScopeTypes;
  name: string;
  description?: string;
  required?: boolean;
  hidden?: boolean;
  dependsOn?: TeamMemberScopeTypes[];
  section?: string;
  order?: number;
};

export const teamMembersScopesDisplay: TeamMemberScopesDisplay = [
  {
    id: "account:write",
    name: "Change Account Data",
    description: "Team member can change account settings.",
    section: "Account",
    order: 10,
  },
  // {
  //   id: "apiKeys:read",
  //   name: "View API Keys",
  //   hidden: true,
  //   description:
  //     "Team member can view API keys that allow access to various Encharge APIs.",
  //   section: "Account",
  //   order: 20,
  // },
  {
    id: "apps:write",
    name: "Connect Apps",
    description:
      "Team member can connect new apps and change settings on already connected apps.",
    section: "Account",
    order: 30,
  },
  // The two MCP & AI switches (Vince, 2026-10-19). Either one lets the member
  // use Settings > MCP & AI; the server checks them on every MCP call
  // (encharge-domain helpers/mcp_access.ts). Write includes read.
  {
    id: "mcp:read",
    name: "MCP: Read only",
    description:
      "Team member can open MCP & AI and connect AI apps that can look but not change anything.",
    section: "Account",
    order: 34,
  },
  {
    id: "mcp:write",
    name: "MCP: Read & Write",
    description: "Team member can connect AI apps that can also make changes.",
    section: "Account",
    order: 35,
  },
  {
    id: "team:read",
    name: "View Team",
    description:
      "Team member can view other team members and their details (including permissions).",
    order: 40,
    section: "Account",
  },
  // {
  //   id: "team:write",
  //   enabled: false,
  //   name: "Manage Team",
  //   description:
  //     "Team member can create, modify and delete other team members.",
  //   hidden: true,
  //   dependsOn: ["team:read"],
  //   order: 50,
  //   section: "Account",
  // },
  {
    id: "billing:read",
    name: "View Billing",
    description: "Team member can see billing details.",
    section: "Billing",
    order: 10,
  },
  {
    id: "billing:write",
    name: "Change Billing",
    description: "Team member can upgrade, downgrade and cancel account.",
    dependsOn: ["billing:read"],
    section: "Billing",
    order: 20,
  },
  {
    id: "emails:read",
    name: "View Emails",
    description: "Team member can view existing emails.",
    order: 10,
    section: "Emails",
  },
  {
    id: "emails:write",
    name: "Change Emails",
    description: "Team member can create new emails and edit existing ones.",
    dependsOn: ["emails:read"],
    order: 20,
    section: "Emails",
  },
  {
    id: "broadcasts:read",
    name: "View Broadcasts",
    description: "Team member can view existing broadcasts.",
    dependsOn: ["emails:read"],
    order: 30,
    section: "Emails",
  },
  {
    id: "broadcasts:write",
    name: "Change Broadcasts",
    description:
      "Team member can send new broadcasts, and modify/cancel existing ones.",
    dependsOn: ["broadcasts:read", "emails:read"],
    order: 40,
    section: "Emails",
  },
  {
    id: "emailMetrics:read",
    name: "View Metrics",
    description:
      "Team member can view email and flow engagement and other metrics.",
    order: 50,
    section: "Emails",
  },
  {
    id: "emailSettings:write",
    name: "Change Email Settings",
    description:
      "Team member can verify emails/domains for sending, and change other email settings.",
    order: 60,
    section: "Emails",
  },
  {
    id: "flows:read",
    name: "View Flows",
    description: "Team member can view existing flows.",
    order: 10,
    section: "Flows",
    dependsOn: ["emails:read"],
  },
  {
    id: "flows:write",
    name: "Change Flows",
    description: "Team member can create new flows, and modify existing ones.",
    dependsOn: ["flows:read", "emails:read"],
    order: 20,
    section: "Flows",
  },
  {
    id: "people:read",
    name: "View People",
    description:
      "Team member can see people, companies and other objects in account.",
    order: 10,
    section: "People",
  },
  {
    id: "people:write",
    name: "Change People",
    description:
      "Team member can create/modify/delete people, companies and other objects.",
    dependsOn: ["people:read"],
    order: 20,
    section: "People",
  },
  {
    id: "people:export",
    name: "Export People",
    description: "Team member can export a list of people in the account.",
    dependsOn: ["people:read"],
    order: 30,
    section: "People",
  },
  {
    id: "personFields:write",
    name: "Change Custom Fields",
    description: "Team member can create, modify and delete Custom Fields.",
    order: 40,
    section: "People",
  },
  {
    id: "forms:read",
    name: "View Forms",
    description: "Team member can view forms and form submissions",
    order: 50,
    section: "Forms",
  },
  {
    id: "forms:write",
    name: "Change Forms",
    description: "Team member can create, modify and delete forms.",
    order: 60,
    dependsOn: ["forms:read"],
    section: "Forms",
  },
  {
    id: "eventManagement:read",
    name: "View Events",
    description: "Team member can view events and event actions.",
    order: 70,
    section: "Events",
  },
  {
    id: "eventManagement:write",
    name: "Change Events",
    description: "Team member can create, modify and delete events.",
    order: 80,
    dependsOn: ["eventManagement:read"],
    section: "Events",
  },
];

export const defaultScopes: TeamMemberScopes = [
  {
    id: "account:write",
    enabled: false,
  },
  // {
  //   id: "apiKeys:read",
  //   enabled: false,
  // },
  {
    id: "apps:write",
    enabled: true,
  },
  // MCP & AI is off for a new member until the owner turns it on
  {
    id: "mcp:read",
    enabled: false,
  },
  {
    id: "mcp:write",
    enabled: false,
  },
  {
    id: "team:read",
    enabled: true,
  },
  // {
  //   id: "team:write",
  //   enabled: false,
  //   name: "Manage Team",
  //   description:
  //     "Team member can create, modify and delete other team members.",
  //   hidden: true,
  //   dependsOn: ["team:read"],
  //   order: 50,
  //   section: "Account",
  // },
  {
    id: "billing:read",
    enabled: false,
  },
  {
    id: "billing:write",
    enabled: false,
  },
  {
    id: "emails:read",
    enabled: true,
  },
  {
    id: "emails:write",
    enabled: true,
  },
  {
    id: "emailMetrics:read",
    enabled: true,
  },
  {
    id: "emailSettings:write",
    enabled: true,
  },
  {
    id: "flows:read",
    enabled: true,
  },
  {
    id: "flows:write",
    enabled: true,
  },
  {
    id: "broadcasts:read",
    enabled: true,
  },
  {
    id: "broadcasts:write",
    enabled: true,
  },
  {
    id: "people:read",
    enabled: true,
  },
  {
    id: "people:write",
    enabled: true,
  },
  {
    id: "people:export",
    enabled: true,
  },
  {
    id: "personFields:write",
    enabled: true,
  },
  {
    id: "forms:read",
    enabled: true,
  },
  {
    id: "forms:write",
    enabled: true,
  },
  {
    id: "eventManagement:read",
    enabled: true,
  },
  {
    id: "eventManagement:write",
    enabled: true,
  },
];
